import { useDebouncedState } from "@modules/shared/hooks/useDebouncedState";
import { useInfiniteSearchProductsQuery } from "@modules/shopify/hooks";
import { useViewAnalytics } from "@modules/shopify/analytics/useViewAnalytics";
import { AnalyticsEventName } from "@shopify/hydrogen-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@ui/components/ui/dialog";
import { Input } from "@ui/components/ui/input";
import { Button } from "@ui/components/ui/button";
import { cn } from "@ui/lib/utils";
import { Search, X } from "lucide-react";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { SearchModalProductsGrid } from "./SearchModalProductsGrid";
import type { SearchProductsQuery } from "@gql/types";
import { useGetCountryVariables } from "@/modules/shared/hooks/useGetCountryVariables";

type Props = {
  show: boolean;
  close: () => void;
};

const OBSERVED_ITEMS_FROM_END = 8;
const SEARCH_PAGE_SIZE = 20;

export const SearchModal: React.FC<Props> = ({ show, close }) => {
  const countryVariables = useGetCountryVariables();
  const { t } = useTranslation("Navbar");
  const [searchQuery, setSearchQuery] = useDebouncedState("", 400);
  const normalizedSearchQuery = useMemo(
    () => searchQuery.trim(),
    [searchQuery],
  );
  useViewAnalytics(
    {
      eventName: AnalyticsEventName.SEARCH_VIEW,
      searchString: normalizedSearchQuery,
    },
    normalizedSearchQuery.length > 1 ? normalizedSearchQuery : null,
  );
  const searchQueryPattern = useMemo(
    () => `title:${normalizedSearchQuery}* OR vendor:${normalizedSearchQuery}*`,
    [normalizedSearchQuery],
  );
  const queryVariables = useMemo(
    () => ({
      query: searchQueryPattern,
      first: SEARCH_PAGE_SIZE,
      ...countryVariables,
    }),
    [countryVariables, searchQueryPattern],
  );

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
  } = useInfiniteSearchProductsQuery(queryVariables, {
    initialPageParam: { cursor: undefined as string | undefined },
    getNextPageParam: (lastPage: SearchProductsQuery) => {
      const pageInfo = lastPage.search.pageInfo;

      if (!pageInfo.hasNextPage || !pageInfo.endCursor) {
        return undefined;
      }

      return { cursor: pageInfo.endCursor };
    },
    enabled: normalizedSearchQuery.length > 2,
    staleTime: 1000 * 60 * 2,
  });

  const products = data?.pages.flatMap((page) => page.search.nodes) ?? [];
  const observedProductHandle =
    products[Math.max(products.length - OBSERVED_ITEMS_FROM_END, 0)]?.handle;

  const handleSearchInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  useEffect(() => {
    setSearchQuery("");
  }, [show]);

  useEffect(() => {
    if (!show || !observedProductHandle || !hasNextPage || isFetchingNextPage) {
      return;
    }

    const candidates = Array.from(
      document.querySelectorAll<HTMLElement>("[data-search-product-handle]"),
    );

    const target = candidates.find(
      (node) => node.dataset.searchProductHandle === observedProductHandle,
    );

    if (!target) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];

        if (entry.isIntersecting) {
          return fetchNextPage();
        }
      },
      {
        root: null,
        threshold: 0,
      },
    );

    observer.observe(target);

    return () => observer.disconnect();
  }, [
    show,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    observedProductHandle,
  ]);

  return (
    <Dialog open={show} onOpenChange={(open) => !open && close()}>
      <DialogContent
        className="border-0 bg-transparent p-0 sm:p-4 md:p-6"
        fullscreen
        showCloseButton={false}
        onClick={(e) => {
          if (e.target === e.currentTarget) close();
        }}
      >
        <DialogTitle className="sr-only">{t("search.placeholder")}</DialogTitle>
        <DialogDescription className="sr-only">
          {t("search.description")}
        </DialogDescription>
        <div
          className={cn(
            "relative mx-auto flex h-full w-full max-w-[1200px] min-h-0 flex-col overflow-hidden bg-background-7 sm:rounded-[32px] sm:border sm:border-border-5 sm:shadow-[0_10px_40px_0] sm:shadow-[#00000014]",
            "grow",
          )}
        >
          <div className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-border-5 bg-background-7/95 px-[calc(100%/40)] pb-4 pt-5 backdrop-blur-xl max-mobile:px-[calc(100%/15)]">
            <img
              src="/logo.svg"
              alt="Logo"
              width={36}
              height={28}
              className="text-primary"
            />
            <Button
              type="button"
              dark="ghost"
              size="unPadded"
              onClick={close}
              className="rounded-lg p-2 transition-colors hover:bg-neutral-100 hover:border-transparent"
              aria-label={t("search.close", "Close search")}
            >
              <X className="size-4" aria-hidden="true" />
            </Button>
          </div>
          <div className="grid grid-cols-40-dynamic px-0 pb-5 pt-5 max-laptop:grid-cols-40-fixed max-mobile:grid-cols-15 max-mobile:px-[calc(100%/15)] *:col-span-padded">
            <Input
              autoFocus
              type="search"
              aria-label={t("search.placeholder")}
              enterKeyHint="search"
              placeholder={t("search.placeholder")}
              variant="box"
              inputSize="large"
              wrapperClassName="col-span-full rounded-full border-border-5 bg-background-7 px-2 shadow-[0_10px_40px_0] shadow-[#00000014]"
              leftIcon={
                <Search
                  className="ml-1 size-5 shrink-0 text-text-4"
                  aria-hidden="true"
                />
              }
              className="bg-transparent font-secondary text-large font-medium text-text-1 placeholder:text-text-5 focus:outline-none max-mobile:text-medium max-mobile:leading-base"
              onChange={handleSearchInput}
            />
          </div>

          <SearchModalProductsGrid
            searchQuery={normalizedSearchQuery}
            products={products}
            loading={isLoading || isFetchingNextPage}
            error={error instanceof Error ? error : null}
            hasNextPage={Boolean(hasNextPage)}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};
